// Pure helpers for the "Forgot password" dialog (Supabase standard recovery with a 6-digit code).
// Dependency-free on purpose (no imports) so it can be tested with
// `node --test src/lib/auth/passwordRecovery.test.mjs` (Node 22.18+ strips the types).
//
// Flow (src/app/sign-up-login-screen/components/LoginForm.tsx):
//   1. supabase.auth.resetPasswordForEmail(email)      -> always the same neutral message
//   2. supabase.auth.verifyOtp({ email, token, type: 'recovery' }) -> one generic error for wrong/expired/used
//   3. supabase.auth.updateUser({ password }) inside that session, then sign out the other sessions and this one.

export const RESEND_COOLDOWN_SECONDS = 60;

export const REQUEST_SENT_MESSAGE =
  'If an account exists for this email address, we have sent a verification code to it.';
export const CODE_INVALID_MESSAGE = 'Invalid or expired verification code.';
export const TOO_MANY_REQUESTS_MESSAGE = 'Too many attempts. Please wait a minute and try again.';
export const NETWORK_MESSAGE = 'Network error. Please try again.';
export const SESSION_EXPIRED_MESSAGE = 'Your reset session has expired. Please start again.';
export const UPDATE_FAILED_MESSAGE = 'Failed to update password. Please try again.';

type AuthErrorLike = { code?: unknown; status?: unknown; message?: unknown; name?: unknown } | null | undefined;

export function normalizeEmail(email: unknown): string {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

/** Same rule as before: something@something.tld, no spaces. Returns an error text or null. */
export function resetEmailError(email: unknown): string | null {
  const e = normalizeEmail(email);
  if (!e) return 'Email is required.';
  if (e.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'Enter a valid email address.';
  return null;
}

/** Exactly 6 digits (the dialog strips everything else while typing). */
export function isSixDigitCode(code: unknown): boolean {
  return typeof code === 'string' && /^\d{6}$/.test(code);
}

/** Existing password rules: required, at least 8 characters, both fields equal. */
export function newPasswordError(password: unknown, confirm: unknown): string | null {
  if (typeof password !== 'string' || password.length === 0) return 'New password is required.';
  if (password.length < 8) return 'Password must be at least 8 characters.';
  if (password !== confirm) return 'Passwords do not match.';
  return null;
}

function isRateLimited(error: AuthErrorLike): boolean {
  const code = typeof error?.code === 'string' ? error.code : '';
  return error?.status === 429 || code.startsWith('over_') || code === 'too_many_requests';
}

function isNetworkError(error: AuthErrorLike): boolean {
  const name = typeof error?.name === 'string' ? error.name : '';
  const message = typeof error?.message === 'string' ? error.message : '';
  return name === 'AuthRetryableFetchError' || name === 'TypeError' || /failed to fetch|network/i.test(message);
}

/**
 * Step 1. Returns an error text to show, or null = show REQUEST_SENT_MESSAGE and go to the code step.
 * Never says whether the email exists: only rate limits and network failures are shown (they do not
 * depend on the email); anything else is treated as sent.
 */
export function requestErrorMessage(error: AuthErrorLike): string | null {
  if (!error) return null;
  if (isRateLimited(error)) return TOO_MANY_REQUESTS_MESSAGE;
  if (isNetworkError(error)) return NETWORK_MESSAGE;
  return null;
}

/** Step 2. Wrong, expired or already used code: one generic text. */
export function verifyErrorMessage(error: AuthErrorLike): string {
  if (isRateLimited(error)) return TOO_MANY_REQUESTS_MESSAGE;
  if (isNetworkError(error)) return NETWORK_MESSAGE;
  return CODE_INVALID_MESSAGE;
}

/** Step 3. Only password-rule errors are shown in detail; the rest is generic. */
export function updateErrorMessage(error: AuthErrorLike): string {
  const code = typeof error?.code === 'string' ? error.code : '';
  if (code === 'same_password') return 'Choose a password different from your current one.';
  if (code === 'weak_password') return 'This password is too weak. Use a longer password with letters and numbers.';
  if (code === 'session_not_found' || code === 'session_expired' || error?.status === 401 || error?.status === 403) {
    return SESSION_EXPIRED_MESSAGE;
  }
  if (isRateLimited(error)) return TOO_MANY_REQUESTS_MESSAGE;
  if (isNetworkError(error)) return NETWORK_MESSAGE;
  return UPDATE_FAILED_MESSAGE;
}

// While the dialog holds a recovery session (between a verified code and the end of the reset), the login
// screen must not redirect the now signed-in user to their dashboard (AuthScreen's AuthenticatedRedirect).
// In memory only: a page reload ends the flow.
let recoveryInProgress = false;
export function setRecoveryInProgress(value: boolean): void {
  recoveryInProgress = value;
}
export function isRecoveryInProgress(): boolean {
  return recoveryInProgress;
}
